import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import {
  AlertCircle,
  Building2,
  Check,
  ChevronDown,
  FileText,
  LayoutGrid,
  ListFilter,
  RefreshCw,
  Search,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  COMPANY_DOCUMENT_CATEGORIES,
  describeAudience,
  formatFileSize,
  getCategoryIcon,
  getCategoryLabel,
  getFileType,
} from "./companyDocumentUtils";

const formatDate = (value) => (value ? format(new Date(value), "dd MMM yyyy") : "—");

/* -------------------------------------------------- */
/* Category tabs (underline, icon + label + count)    */
/* -------------------------------------------------- */
// Only categories that actually hold documents get a tab — an empty tab is a
// dead end. The selected category keeps its tab even if it empties out.
const useCategoryOptions = (documents, value) =>
  useMemo(() => {
    const counts = {};
    documents.forEach((d) => {
      counts[d.category] = (counts[d.category] || 0) + 1;
    });
    return [
      { value: "all", label: "All Documents", icon: LayoutGrid, count: documents.length },
      ...COMPANY_DOCUMENT_CATEGORIES.filter((c) => counts[c.value] || c.value === value).map((c) => ({
        ...c,
        count: counts[c.value] || 0,
      })),
    ];
  }, [documents, value]);

const TAB_BASE =
  "-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-t-md";
const TAB_GAP_PX = 4; // gap-1

const TabLabel = ({ tab, selected }) => {
  const Icon = tab.icon;
  return (
    <>
      <Icon className="h-4 w-4" />
      {tab.label}
      <span
        className={cn(
          "rounded-full px-1.5 text-xs tabular-nums",
          selected ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
        )}
      >
        {tab.count}
      </span>
    </>
  );
};

// Tablet and desktop. Shows as many tabs as fit and folds the rest into a
// "More" menu, re-measured on resize — nine categories do not fit beside the
// admin sidebar even on a wide screen. The selected category is always kept
// visible. Phones get CompanyDocumentCategoryFilter instead.
export function CompanyDocumentCategoryTabs({ documents, value, onChange }) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const tabs = useCategoryOptions(documents, value);
  // { widths: px per tab (same order as `tabs`), moreWidth, available }
  const [metrics, setMetrics] = useState(null);

  const tabKey = tabs.map((t) => `${t.value}:${t.count}`).join("|");

  // Layout effect so the first paint already has the right split (no flash of
  // an overflowing row).
  useLayoutEffect(() => {
    const container = containerRef.current;
    const measurer = measureRef.current;
    if (!container || !measurer) return undefined;

    const measure = () => {
      const available = container.clientWidth;
      if (!available) return; // hidden at the phone breakpoint
      const nodes = [...measurer.children];
      setMetrics({
        widths: nodes.slice(0, -1).map((n) => n.offsetWidth),
        moreWidth: nodes[nodes.length - 1].offsetWidth,
        available,
      });
    };

    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [tabKey]);

  const { visible, overflow } = useMemo(() => {
    if (!metrics || metrics.widths.length !== tabs.length) return { visible: tabs, overflow: [] };
    const { widths, moreWidth, available } = metrics;
    const total = widths.reduce((sum, w, i) => sum + w + (i ? TAB_GAP_PX : 0), 0);
    if (total <= available) return { visible: tabs, overflow: [] };

    // Reserve the More button and the selected tab first, then fill in order.
    const selectedIndex = tabs.findIndex((t) => t.value === value);
    const keep = new Set();
    let used = moreWidth;
    if (selectedIndex !== -1) {
      keep.add(selectedIndex);
      used += widths[selectedIndex] + TAB_GAP_PX;
    }
    for (let i = 0; i < tabs.length; i += 1) {
      if (keep.has(i)) continue;
      if (used + widths[i] + TAB_GAP_PX > available) break;
      keep.add(i);
      used += widths[i] + TAB_GAP_PX;
    }
    return {
      visible: tabs.filter((_, i) => keep.has(i)),
      overflow: tabs.filter((_, i) => !keep.has(i)),
    };
  }, [metrics, tabs, value]);

  return (
    <div ref={containerRef} className="relative hidden border-b border-border sm:block">
      {/* Off-screen copy of every tab (+ the More button) used only for measuring. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute left-0 top-0 flex w-max gap-1"
      >
        {tabs.map((tab) => (
          <span key={tab.value} className={cn(TAB_BASE, "border-transparent")}>
            <TabLabel tab={tab} selected={false} />
          </span>
        ))}
        <span className={cn(TAB_BASE, "border-transparent")}>
          More <span className="rounded-full px-1.5 text-xs">99</span>
          <ChevronDown className="h-4 w-4" />
        </span>
      </div>

      <div role="tablist" aria-label="Document categories" className="flex gap-1">
        {visible.map((tab) => {
          const selected = value === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(tab.value)}
              className={cn(
                TAB_BASE,
                selected
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border",
              )}
            >
              <TabLabel tab={tab} selected={selected} />
            </button>
          );
        })}

        {overflow.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  TAB_BASE,
                  "border-transparent text-muted-foreground hover:text-foreground hover:border-border",
                )}
              >
                More
                <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums text-muted-foreground">
                  {overflow.length}
                </span>
                <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 p-1.5">
              {overflow.map((tab) => {
                const Icon = tab.icon;
                return (
                  <DropdownMenuItem
                    key={tab.value}
                    onClick={() => onChange(tab.value)}
                    className="flex cursor-pointer items-center gap-2.5 py-2"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="flex-1 truncate">{tab.label}</span>
                    <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums text-muted-foreground">
                      {tab.count}
                    </span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------- */
/* Phone category filter: button + bottom sheet       */
/* -------------------------------------------------- */
export function CompanyDocumentCategoryFilter({ documents, value, onChange }) {
  const [open, setOpen] = useState(false);
  const options = useCategoryOptions(documents, value);
  const active = value !== "all";

  const choose = (next) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Drawer open={open} onOpenChange={setOpen} shouldScaleBackground={false}>
      <DrawerTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={active ? `Category: ${getCategoryLabel(value)}. Change category` : "Filter by category"}
          className={cn(
            "relative h-10 w-10 shrink-0 sm:hidden",
            active && "border-primary text-primary",
          )}
        >
          <ListFilter className="h-4 w-4" />
          {active && (
            <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-background bg-primary" />
          )}
        </Button>
      </DrawerTrigger>

      <DrawerContent className="max-h-[80dvh]">
        <DrawerHeader className="pb-2 text-left">
          <DrawerTitle>Filter by category</DrawerTitle>
          <DrawerDescription>Showing documents from one category at a time.</DrawerDescription>
        </DrawerHeader>

        <div
          role="listbox"
          aria-label="Document categories"
          className="overflow-y-auto px-3"
          style={{ paddingBottom: "calc(1rem + var(--safe-area-inset-bottom, 0px))" }}
        >
          {options.map((opt) => {
            const Icon = opt.icon;
            const selected = value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => choose(opt.value)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
                  selected ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted",
                )}
              >
                <span
                  className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                    selected ? "bg-primary/15" : "bg-primary/10 text-primary",
                  )}
                >
                  <Icon className="h-[18px] w-[18px]" />
                </span>
                <span className="min-w-0 flex-1 truncate text-[15px] font-medium">{opt.label}</span>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-xs tabular-nums",
                    selected ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground",
                  )}
                >
                  {opt.count}
                </span>
                <Check className={cn("h-4 w-4 shrink-0", selected ? "opacity-100" : "opacity-0")} />
              </button>
            );
          })}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

// Phone only: shows which category is applied, with one-tap clear.
export function CompanyDocumentActiveCategory({ value, count, onClear }) {
  if (value === "all") return null;
  const Icon = getCategoryIcon(value);
  return (
    <div className="flex items-center justify-between gap-2 sm:hidden">
      <button
        type="button"
        onClick={onClear}
        aria-label={`Clear category filter ${getCategoryLabel(value)}`}
        className="inline-flex min-w-0 items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 py-1 pl-2.5 pr-2 text-sm font-medium text-primary"
      >
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{getCategoryLabel(value)}</span>
        <X className="h-3.5 w-3.5 shrink-0" />
      </button>
      <span className="shrink-0 text-xs text-muted-foreground">
        {count} document{count === 1 ? "" : "s"}
      </span>
    </div>
  );
}

/* -------------------------------------------------- */
/* Search (+ phone filter, + optional right slot)     */
/* -------------------------------------------------- */
export function CompanyDocumentsToolbar({ searchTerm, onSearchChange, filter = null, children }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex w-full items-center gap-2 sm:max-w-sm">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
            aria-label="Search documents"
          />
        </div>
        {filter}
      </div>
      {children}
    </div>
  );
}

/* -------------------------------------------------- */
/* Segmented control (e.g. Active / Archived)         */
/* -------------------------------------------------- */
export function SegmentedControl({ options, value, onChange, ariaLabel }) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="flex w-full rounded-lg border border-border bg-muted/50 p-1 sm:inline-flex sm:w-auto"
    >
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(opt.value)}
            className={cn(
              "flex-1 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors sm:flex-none",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              selected
                ? "bg-card text-primary shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const AUDIENCE_ICONS = { all: Users, departments: Building2, selected: UserCheck };

function AudienceIcon({ mode }) {
  const Icon = AUDIENCE_ICONS[mode] || Users;
  return <Icon className="h-3.5 w-3.5 shrink-0 text-primary" />;
}

/* -------------------------------------------------- */
/* Document card                                      */
/* -------------------------------------------------- */
function DocumentCard({ doc, onOpen, renderActions, renderMenu, renderStatus, showAudit }) {
  const CategoryIcon = getCategoryIcon(doc.category);
  const type = getFileType(doc.mime_type);
  const archived = Boolean(doc.archived_at);
  const audienceText = doc.audience ? describeAudience(doc.audience) : null;
  const dateLabel = archived ? "Archived" : "Updated";
  const dateValue = formatDate(archived ? doc.archived_at : doc.updated_at);

  return (
    <div
      className={cn(
        "group relative flex min-w-0 flex-col rounded-xl border p-3.5 transition-all sm:p-5",
        "border-primary/15 bg-primary/[0.03] hover:border-primary/40 hover:shadow-sm",
        "dark:border-primary/20 dark:bg-primary/[0.06] dark:hover:border-primary/50",
      )}
    >
      {renderMenu && <div className="absolute right-1.5 top-1.5 sm:right-2 sm:top-2">{renderMenu(doc)}</div>}

      {/* Phone: icon beside the title. sm+: icon above it. */}
      <div className="flex items-center gap-3 sm:flex-col sm:items-start sm:gap-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary sm:h-10 sm:w-10">
          <CategoryIcon className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
        </div>
        <button
          type="button"
          onClick={() => onOpen(doc)}
          className={cn(
            "min-w-0 flex-1 text-left text-[15px] font-semibold leading-snug text-foreground hover:text-primary focus-visible:outline-none focus-visible:underline line-clamp-2 sm:w-full sm:text-base sm:line-clamp-1",
            renderMenu && "pr-8",
          )}
          title={doc.title}
        >
          {doc.title}
        </button>
      </div>

      {doc.description ? (
        <p className="mt-2 text-sm text-muted-foreground line-clamp-2 sm:mt-1 sm:min-h-[2.5rem]" title={doc.description}>
          {doc.description}
        </p>
      ) : (
        <p className="mt-1 hidden text-sm italic text-muted-foreground/70 sm:block sm:min-h-[2.5rem]">No description</p>
      )}

      {/* Phone: the same facts as short lines instead of a labelled grid. */}
      <div className="mt-2 min-w-0 space-y-1 text-xs text-muted-foreground sm:hidden">
        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <span className="font-medium uppercase text-foreground">{type.label}</span>
          <span aria-hidden="true">·</span>
          <span className="whitespace-nowrap">{formatFileSize(doc.file_size)}</span>
          <span aria-hidden="true">·</span>
          <span className="whitespace-nowrap">
            {dateLabel} {dateValue}
          </span>
        </p>
        {showAudit && (
          <p className="truncate">
            By {doc.uploaded_by?.name || "—"} · Uploaded {formatDate(doc.created_at)}
          </p>
        )}
        {audienceText && (
          <p className="flex min-w-0 items-center gap-1.5" title={audienceText}>
            <AudienceIcon mode={doc.audience.mode} />
            <span className="truncate">{audienceText}</span>
          </p>
        )}
      </div>

      <div className="hidden min-w-0 flex-1 flex-col sm:flex">
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          <div className="min-w-0">
            <dt className="text-muted-foreground">File</dt>
            <dd className="mt-0.5 truncate font-medium text-foreground" title={doc.file_name}>
              <span className="uppercase">{type.label}</span> · {formatFileSize(doc.file_size)}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-muted-foreground">{archived ? "Archived" : showAudit ? "Last updated" : "Updated"}</dt>
            <dd className="mt-0.5 truncate font-medium text-foreground">
              {formatDate(archived ? doc.archived_at : doc.updated_at)}
            </dd>
          </div>
          {showAudit && (
            <>
              <div className="min-w-0">
                <dt className="text-muted-foreground">Uploaded by</dt>
                <dd className="mt-0.5 truncate font-medium text-foreground" title={doc.uploaded_by?.name}>
                  {doc.uploaded_by?.name || "—"}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-muted-foreground">Uploaded</dt>
                <dd className="mt-0.5 truncate font-medium text-foreground">{formatDate(doc.created_at)}</dd>
              </div>
            </>
          )}
          {doc.audience && (
            <div className="col-span-2 min-w-0">
              <dt className="text-muted-foreground">Visible to</dt>
              <dd
                className="mt-0.5 flex items-center gap-1.5 font-medium text-foreground"
                title={describeAudience(doc.audience)}
              >
                <AudienceIcon mode={doc.audience.mode} />
                <span className="truncate">{describeAudience(doc.audience)}</span>
              </dd>
            </div>
          )}
        </dl>
      </div>

      {renderStatus?.(doc)}

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-primary/10 pt-2.5 sm:mt-auto sm:border-0 sm:pt-4">
        <span className="inline-flex min-w-0 max-w-full items-center truncate rounded-md border border-primary/20 bg-card px-2 py-0.5 text-xs font-medium text-primary">
          {getCategoryLabel(doc.category)}
        </span>
        <div className="-mr-1.5 flex shrink-0 items-center gap-0.5 sm:mr-0">{renderActions(doc)}</div>
      </div>
    </div>
  );
}

/* -------------------------------------------------- */
/* Grid + client pagination + empty state             */
/* -------------------------------------------------- */
const PAGE_SIZES = [12, 24, 48];

export function CompanyDocumentsGrid({
  documents,
  onOpen,
  renderActions,
  renderMenu,
  renderStatus,
  showAudit = false,
  emptyTitle,
  emptyDescription,
  emptyAction = null,
  isFiltered = false,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

  useEffect(() => {
    setCurrentPage(1);
  }, [documents, pageSize]);

  const totalPages = Math.max(1, Math.ceil(documents.length / pageSize));
  const page = useMemo(
    () => documents.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [documents, currentPage, pageSize],
  );

  if (documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-primary/25 bg-primary/[0.02] px-4 py-16 text-center dark:bg-primary/[0.04]">
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <FileText className="h-7 w-7" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">{isFiltered ? "No matching documents" : emptyTitle}</h3>
        <p className="mt-2 max-w-sm text-sm text-muted-foreground">
          {isFiltered
            ? "Try a different search term or category."
            : emptyDescription}
        </p>
        {!isFiltered && emptyAction && <div className="mt-5">{emptyAction}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {page.map((doc) => (
          <DocumentCard
            key={doc._id}
            doc={doc}
            onOpen={onOpen}
            renderActions={renderActions}
            renderMenu={renderMenu}
            renderStatus={renderStatus}
            showAudit={showAudit}
          />
        ))}
      </div>

      {documents.length > PAGE_SIZES[0] && (
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex w-full items-center justify-between gap-3 text-sm md:w-auto md:justify-start">
            <span className="text-muted-foreground">Per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="h-8 w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-full items-center justify-between gap-3 md:w-auto md:justify-end">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Prev
            </Button>
            <span className="whitespace-nowrap text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------- */
/* Image preview                                      */
/* -------------------------------------------------- */
export function CompanyDocumentPreviewDialog({ preview, onClose }) {
  return (
    <Dialog open={Boolean(preview)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[95vw] sm:max-w-4xl dark:border-foreground/40">
        <DialogHeader>
          <DialogTitle className="truncate pr-6 text-base sm:text-lg">
            {preview?.doc?.title || "Preview"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex max-h-[70vh] items-center justify-center overflow-auto rounded-md bg-muted/40 dark:bg-muted/20">
          {preview?.url && (
            <img
              src={preview.url}
              alt={preview.doc?.title || "Document preview"}
              className="max-h-[70vh] max-w-full object-contain"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------- */
/* Error state                                        */
/* -------------------------------------------------- */
export function CompanyDocumentsError({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border bg-muted/30 px-4 py-16 text-center">
      <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 dark:bg-red-950/40">
        <AlertCircle className="h-7 w-7 text-red-600 dark:text-red-400" />
      </div>
      <h3 className="text-lg font-semibold text-foreground">Couldn't load documents</h3>
      <p className="mt-2 max-w-sm text-sm text-muted-foreground">{message}</p>
      <Button variant="outline" className="mt-5" onClick={onRetry}>
        <RefreshCw className="mr-2 h-4 w-4" />
        Try again
      </Button>
    </div>
  );
}

/* -------------------------------------------------- */
/* Skeleton                                           */
/* -------------------------------------------------- */
export function CompanyDocumentsSkeleton({ withAction = false }) {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-4 w-72" />
        </div>
        {withAction && <Skeleton className="h-10 w-44" />}
      </div>
      <div className="flex gap-4 border-b border-border pb-3">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-5 w-28" />
        ))}
      </div>
      <Skeleton className="h-10 w-full sm:max-w-sm" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="rounded-xl">
            <CardContent className="space-y-3 p-5">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-5 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
